// #region Imports
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Tab from '@mui/material/Tab';
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import {
  ConfirmResponse,
  useConfirmDialog
} from '_shared/_providers/dialogs/confirm/confirm-dialog';
import { useDialog } from '_shared/_providers/dialogs/dialog';
import CondensedCell from '_shared/_views/tables/condensed-cell';
import { NoValueDash } from '_shared/_views/tables/no-value-dash';
import RowActions from '_shared/_views/tables/row-actions';
import RowLink from '_shared/_views/tables/row-link';
import SearchMatch from '_shared/_views/tables/search-match';
import { MRTNoMemo } from '_shared/_views/tables/table';
import TruncatedCell from '_shared/_views/tables/truncated-cell';
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from '_shared/guids/guid';
import IndividualAvatar from 'marketplace/_views/avatars/individual-avatar';
import {
  useDeleteIndividualBookmarkMutation,
  useGetIndividualBookmarksQuery,
} from 'marketplace/bookmarks/_api/bookmark-api';
import { PageTab } from 'marketplace/bookmarks/_views/bookmarks-page-tabs';
import IndividualBookmarkQuery, {
  INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH,
  IndividualBookmarkOrderField,
} from 'marketplace/bookmarks/individual-bookmarks/_api/queries/individual-bookmark-query';
import BookmarkedIndividualListItem from 'marketplace/bookmarks/individual-bookmarks/bookmarked-individual-list-item';
import ViewIndividualProfile from 'marketplace/individuals/profiles/_views/individual-profile-container';
import {
  createMRTColumnHelper,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_Updater,
} from "material-react-table";
import {
  MobileToolbarButtonsContainer,
  ScrollableTabs,
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { enqueueSnackbar } from '_shared/_providers/snackbars/snackbars';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { DateCell } from "_shared/_views/tables/date-cells";
import { formatDate } from "_shared/formatting/dates";
// #endregion

// #region Styled Components
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.down('md')]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const NameCellContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  minWidth: 0,
}));
// #endregion

// #region Component Input Props
type IndividualBookmarksProps = {
  activeTab: PageTab;
}

const columnHelper = createMRTColumnHelper<BookmarkedIndividualListItem>();

/** The sortable columns, by the ids the sort state carries. */
export const NAME_COLUMN = "name";
export const DATE_BOOKMARKED_COLUMN = "created";

/** The person's email. It neither sorts nor filters, and folds under the name when the table is narrow. */
export const EMAIL_COLUMN = "email";

/** The name a row shows when the server does not let the caller see the bookmarked person. */
export const NOT_VISIBLE_NAME = "Not visible to you";

/** Newest bookmark first: the server's own default, so the header arrow says what the rows are in. */
export const DEFAULT_SORTING: SortingState = [{ id: DATE_BOOKMARKED_COLUMN, desc: true }];

/**
 * The order the server is asked for, from the order the table shows.
 *
 * Always one column and always a direction. The server orders every page, so an "unsorted" state
 * would be a header arrow that means nothing. `enableSortingRemoval` is off for the same reason.
 */
export function individualBookmarkOrderFrom(sorting: SortingState): {
  orderBy: IndividualBookmarkOrderField;
  orderDir: OrderDirection;
} {
  const first = sorting.length > 0 ? sorting[0] : DEFAULT_SORTING[0];
  return {
    orderBy: first.id === NAME_COLUMN
      ? IndividualBookmarkOrderField.Name
      : IndividualBookmarkOrderField.DateBookmarked,
    orderDir: first.desc ? OrderDirection.Descending : OrderDirection.Ascending,
  };
}

/** Whether the server accepts the search. It refuses a search longer than it can match. */
export function isIndividualBookmarksGlobalFilterValid(value: string): boolean {
  return value.trim().length <= INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH;
}

/** True when the row can open the bookmarked person's profile. A row the caller may not see cannot. */
function canViewProfile(row: BookmarkedIndividualListItem): boolean {
  return !row.isUnavailable && row.individual !== undefined;
}

/** The person's email, or nothing for a row the caller may not see or a loading placeholder. */
function emailOf(row: BookmarkedIndividualListItem): string {
  return row.individual?.email?.value ?? "";
}

/** MRT hands state changes as either a value or a function of the old one. */
function applyUpdater<TState>(updater: MRT_Updater<TState>, current: TState): TState {
  return typeof updater === "function" ? (updater as (old: TState) => TState)(current) : updater;
}

export default function IndividualBookmarks(props: Readonly<IndividualBookmarksProps>) {
  const { activeTab } = props;

  // #region State
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  // #region Hooks
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // #region Queries
  const isGlobalFilterValid = isIndividualBookmarksGlobalFilterValid(globalFilter);

  // No debounce here. MRT's own `GlobalFilterTextField` debounces the typed text and pushes it into
  // `globalFilter` after 500ms while `manualFiltering` is on. A second debounce would make the
  // reader wait twice.
  const query = useMemo(() => {
    if (!isGlobalFilterValid) return undefined;

    const order = individualBookmarkOrderFrom(sorting);
    return new IndividualBookmarkQuery({
      paginationParameters: PaginationParameters.create(pagination),
      orderBy: order.orderBy,
      orderDir: order.orderDir,
      query: globalFilter,
    });
  }, [globalFilter, isGlobalFilterValid, pagination, sorting]);

  // The page comes from the query's own result, not from the bookmarks slice. The slice keeps one
  // entry for each search, which every page of that search shares, so a page that RTK Query serves
  // from its cache (no new request) would leave the slice showing the page the reader left.
  const bookmarksQuery = useGetIndividualBookmarksQuery(query ? { query } : skipToken);
  const [deleteIndividualBookmark, { isLoading: isDeleting }] = useDeleteIndividualBookmarkMutation();

  const bookmarks = useMemo(() => {
    if (!bookmarksQuery.data) return PaginatedResponse.empty<BookmarkedIndividualListItem>();
    return PaginatedResponse.from<BookmarkedIndividualListItem>(
      BookmarkedIndividualListItem.fromJSONArray(bookmarksQuery.data.data),
      bookmarksQuery.data.headers,
    );
  }, [bookmarksQuery.data]);
  const bookmarksError = bookmarksQuery.isError;

  // #region Lifecycle Hooks
  useEffect(() => {
    if (bookmarksError) {
      enqueueSnackbar("Error loading bookmarks. Please try again.", { variant: 'error' });
    }
  }, [bookmarksError]);

  // #region Event Handlers
  async function handleDeleteBookmark(row: BookmarkedIndividualListItem) {
    const response = await confirm({
      title: 'Delete Bookmark?',
      message: 'Do you want to delete?',
      okButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteIndividualBookmark({
        bookmarkId: row.bookmark.id.value,
        individualId: row.bookmark.individualId.value,
      }).unwrap();
      enqueueSnackbar("Deleted bookmark", { variant: 'success' });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't delete bookmark. Please try again", { variant: 'error' });
    }
  }

  // Memoized because the name cell's link depends on it, and a fresh function each render would
  // rebuild that cell — and through it the column list — on every render.
  const handleViewProfile = useCallback((profileId: Guid): void => {
    openDialog({
      component: (
        <ViewIndividualProfile
          individualId={profileId}
        />
      ),
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: 'flex',
        overflowX: 'hidden'
      },
      MuiProps: {
        maxWidth: 'lg',
        fullWidth: false
      }
    });
  }, [openDialog]);

  /**
   * What opening the row does: the row's click, the name's link and the ⋯'s Open all come from here.
   * Nothing for a row the caller may not see.
   */
  const profileOpenerOf = useCallback((row: BookmarkedIndividualListItem): (() => void) | undefined => {
    if (!canViewProfile(row)) return undefined;
    return () => handleViewProfile(row.individualId);
  }, [handleViewProfile]);
  // #endregion

  // #region Columns
  const renderNameColumn = useCallback((
    row: BookmarkedIndividualListItem,
    table: MRT_TableInstance<BookmarkedIndividualListItem>,
  ) => {
    const name = row.individual?.fullName ?? NOT_VISIBLE_NAME;
    const company = row.individual?.entityName;

    // The company reads under the name, rather than in a column of its own (#6722).
    return (
      <NameCellContainer>
        <IndividualAvatar
          individual={row.individual}
        />
        <CondensedCell
          title={
            // A row the caller may not see opens nothing, so it must not look like a link.
            // The server searches the name of anyone it discloses, never the stand-in (#6590).
            <RowLink onClick={profileOpenerOf(row)}>
              {row.individual ? <SearchMatch table={table} text={name} /> : name}
            </RowLink>
          }
          // The server searches the company's name too.
          secondary={company && <SearchMatch table={table} text={company} />}
        />
      </NameCellContainer>
    );
  }, [profileOpenerOf]);

  const columns = useMemo(() => [
    // Accessor columns, so that MRT shows a sort arrow. The server sorts: the name by last name,
    // then first name, and the date by when the bookmark was made.
    columnHelper.accessor((row) => row.individual?.fullName ?? NOT_VISIBLE_NAME, {
      header: "Name",
      id: NAME_COLUMN,
      enableSorting: true,
      meta: { kind: "name" },
      Cell: ({ row, table }) => renderNameColumn(row.original, table),
    }),
    columnHelper.accessor((row) => emailOf(row), {
      header: "Email",
      id: EMAIL_COLUMN,
      // The server searches names, companies and practice areas, never the email (#6590).
      enableGlobalFilter: false,
      enableSorting: false,
      meta: {
        kind: "text",
        // Short of room, the email reads under the name before the table scrolls (#6722).
        fold: { into: NAME_COLUMN, priority: 1, render: (row) => emailOf(row) || null },
      },
      Cell: ({ row }) => {
        const email = emailOf(row.original);
        return email ? <TruncatedCell>{email}</TruncatedCell> : <NoValueDash />;
      },
    }),
    columnHelper.accessor((row) => formatDate(row.bookmark?.createdDate) ?? "", {
      header: "Bookmarked",
      id: DATE_BOOKMARKED_COLUMN,
      Cell: ({ row }) => <DateCell value={row.original.bookmark?.createdDate} />,
      // The search box never matches the date, so the date never shows a match (#6590).
      enableGlobalFilter: false,
      enableSorting: true,
      meta: { kind: "date" },
    }),
  ], [renderNameColumn]);
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<BookmarkedIndividualListItem> = {
    columns,
    data: bookmarks.data,
    enableRowSelection: false,
    enableTableHead: true,
    enableColumnPinning: true,
    enableRowActions: true,
    muiTableBodyRowProps: ({ row }) => {
      const openProfile = profileOpenerOf(row.original);
      if (!openProfile) return {};
      return { onClick: openProfile, sx: { cursor: "pointer" } };
    },
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <TableToolsMenu table={table} />
            </ToolbarButtons>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">
            Bookmarks
          </TableTitle>
          <LowerTopToolbarContainer>
            <ScrollableTabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue) => navigate(`/marketplace/bookmarks/${newValue}`)}>
              <Tab value={PageTab.Individual} label="Individual" />
              <Tab value={PageTab.Team} label="Team" />
              <Tab value={PageTab.Company} label="Company" />
            </ScrollableTabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <TableToolsMenu table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    // While a page loads, MRT fills the table with empty placeholder rows ({}), which have no bookmark.
    getRowId: (row, index) => row.bookmark?.id.value ?? `placeholder-${index}`,
    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ['mrt-row-actions'] }
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) => ({
      ...searchTextFieldProps({
        searches: "individual bookmarks",
        table,
        maxLength: INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH,
      }),
      error: !isGlobalFilterValid,
      helperText: isGlobalFilterValid
        ? undefined
        : `Search must be ${INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH} characters or fewer.`,
    }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: bookmarksError,
      isLoading: bookmarksQuery.isFetching,
      onRetry: () => void bookmarksQuery.refetch(),
      subject: "individual bookmarks",
    }),
    renderRowActions: ({ row }) => {
      const individualName = row.original.individual?.fullName ?? "individual";
      const openProfile = profileOpenerOf(row.original);
      return (
        <RowActions
          ariaLabel={`Actions for ${individualName}`}
          open={openProfile && { onClick: openProfile }}
          actions={[
            {
              key: "delete",
              label: "Delete Bookmark",
              ariaLabel: `Delete bookmark for ${individualName}`,
              icon: <DeleteIcon />,
              destructive: true,
              onClick: () => {
                void handleDeleteBookmark(row.original);
              },
            },
          ]}
        />
      );
    },
    // The server pages, searches and sorts. No column filters: the search box does that work.
    enableColumnFilters: false,
    enableMultiSort: false,
    // The server orders every page, so there is no unsorted state for a third click to mean.
    enableSortingRemoval: false,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: bookmarksError
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount: bookmarks.totalElements,
    // A new search or a new order is a new result set, so the reader goes back to its first page.
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setGlobalFilter(applyUpdater(updater, globalFilter) ?? "");
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onPaginationChange: (updater: MRT_Updater<PaginationState>) => {
      const next = applyUpdater(updater, pagination);
      // A different page size is a different set of pages, so the reader goes back to the first.
      setPagination(next.pageSize === pagination.pageSize ? next : { ...next, pageIndex: 0 });
    },
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    state: {
      globalFilter,
      isLoading: bookmarksQuery.isLoading || isDeleting,
      pagination,
      showAlertBanner: bookmarksError,
      showProgressBars: bookmarksQuery.isFetching || isDeleting,
      showSkeletons: bookmarksQuery.isLoading,
      sorting,
    },
  };

  return <MRTNoMemo<BookmarkedIndividualListItem> options={tableOptions} persistColumnVisibilityAs="marketplace-bookmarks-individuals" cards={{ defaultSorting: DEFAULT_SORTING }} />;
}
